import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Container,
    Paper,
    Typography,
    Stack,
    Grid,
    TextField,
    IconButton,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Card,
    CardMedia
} from '@mui/material';
import {
    Videocam as VideocamIcon,
    Stop as StopIcon,
    SwitchCamera as SwitchCameraIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    VideocamOff as VideocamOffIcon,
    Send as SendIcon,
    Favorite as FavoriteIcon,
    Visibility as VisibilityIcon,
    PinDrop as PinIcon,
    PushPin as PushPinIcon
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import { apiUrl } from '../../comp/ApiUrl';

const SOCKET_SERVER_URL = apiUrl; // Altere para a URL do seu backend Node.js

const LiveStreamLojista = () => {
    const [canal, setCanal] = useState('loja_01');
    const [isStreaming, setIsStreaming] = useState(false);
    const [statusText, setStatusText] = useState('Aguardando início...');
    const [statusColor, setStatusColor] = useState('text.secondary');
    const [secondsElapsed, setSecondsElapsed] = useState(0);
    const [usingFrontCamera, setUsingFrontCamera] = useState(true);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [videoEnabled, setVideoEnabled] = useState(true);
    const [viewersCount, setViewersCount] = useState(0);
    const [likesCount, setLikesCount] = useState(0);

    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');

    const [produtos] = useState([
        { id: 1, nome: 'Vestido Florido Verão', preco: 'R$ 129,90', img: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=150' },
        { id: 2, nome: 'Conjunto Blusa + Saia', preco: 'R$ 189,90', img: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=150' }
    ]);
    const [pinnedProduct, setPinnedProduct] = useState(null);

    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const timerIntervalRef = useRef(null);
    const videoRef = useRef(null);
    const chatBottomRef = useRef(null);
    const socketRef = useRef(null);

    useEffect(() => {
        return () => {
            pararTransmissao();
        };
    }, []);

    useEffect(() => {
        chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const conectarSocket = () => {
        if (socketRef.current) socketRef.current.disconnect();

        const socket = io(SOCKET_SERVER_URL, { transports: ['websocket'] });
        socketRef.current = socket;

        socket.emit('entrar_live', {
            canal: canal.trim(),
            usuario: 'Lojista',
            isLojista: true
        });

        socket.on('estado_inicial_live', (data) => {
            setLikesCount(data.curtidas || 0);
            setPinnedProduct(data.produtoFixado || null);
            if (data.mensagens) setMessages(data.mensagens);
        });

        socket.on('atualizar_espectadores', ({ espectadores }) => {
            setViewersCount(espectadores);
        });

        socket.on('atualizar_curtidas', ({ curtidas }) => {
            setLikesCount(curtidas);
        });

        socket.on('nova_mensagem', (mensagem) => {
            setMessages((prev) => [...prev, mensagem]);
        });

        socket.on('produto_fixado_alterado', ({ produto }) => {
            setPinnedProduct(produto);
        });
    };

    const desconectarSocket = () => {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    };

    const startTimer = () => {
        setSecondsElapsed(0);
        timerIntervalRef.current = setInterval(() => {
            setSecondsElapsed((prev) => prev + 1);
        }, 1000);
    };

    const stopTimer = () => {
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setSecondsElapsed(0);
    };

    const formatTime = (totalSeconds) => {
        const hrs = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const mins = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const secs = String(totalSeconds % 60).padStart(2, '0');
        return `${hrs}:${mins}:${secs}`;
    };

    const getStream = async (useFront = true) => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((track) => track.stop());
        }

        const constraints = {
            video: {
                width: { ideal: 1280, max: 1280 },
                height: { ideal: 720, max: 720 },
                frameRate: { ideal: 24, max: 24 },
                facingMode: useFront ? 'user' : 'environment'
            },
            audio: { echoCancellation: true, noiseSuppression: true }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        localStreamRef.current = stream;

        if (videoRef.current) {
            videoRef.current.srcObject = stream;
        }
        return stream;
    };

    const iniciarTransmissao = async () => {
        if (!canal.trim()) {
            alert('Por favor, informe o nome do canal/loja!');
            return;
        }

        try {
            setStatusText('Acessando câmera...');
            setStatusColor('info.main');

            const stream = await getStream(usingFrontCamera);

            setStatusText('Conectando ao servidor...');

            const pc = new RTCPeerConnection({
                iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
            });
            pcRef.current = pc;

            pc.onconnectionstatechange = () => {
                if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
                    setStatusText('⚠️ Instabilidade na rede!');
                    setStatusColor('warning.main');
                } else if (pc.connectionState === 'connected') {
                    setStatusText('🔴 AO VIVO!');
                    setStatusColor('error.main');
                }
            };

            stream.getTracks().forEach((track) => pc.addTrack(track, stream));

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            const tokenJwt = localStorage.getItem('fazpedido_token') || '';
            const serverUrl = `https://whip.fazpedido.app/${canal.trim()}/whip${tokenJwt ? `?token=${tokenJwt}` : ''}`;

            const response = await fetch(serverUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/sdp' },
                body: pc.localDescription.sdp
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error('Falha no servidor: ' + errorText);
            }

            const answerSdp = await response.text();
            await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

            setIsStreaming(true);
            setStatusText('🔴 AO VIVO!');
            setStatusColor('error.main');
            setAudioEnabled(true);
            setVideoEnabled(true);

            conectarSocket();
            startTimer();
        } catch (err) {
            console.error(err);
            setStatusText('Erro: ' + err.message);
            setStatusColor('error.main');
        }
    };

    const trocarCamera = async () => {
        const nextState = !usingFrontCamera;
        setUsingFrontCamera(nextState);

        const newStream = await getStream(nextState);
        if (pcRef.current && newStream) {
            const videoTrack = newStream.getVideoTracks()[0];
            const sender = pcRef.current.getSenders().find((s) => s.track && s.track.kind === 'video');
            if (sender) sender.replaceTrack(videoTrack);
        }
    };

    const toggleAudio = () => {
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !audioTrack.enabled;
                setAudioEnabled(audioTrack.enabled);
            }
        }
    };

    const toggleVideo = () => {
        if (localStreamRef.current) {
            const videoTrack = localStreamRef.current.getVideoTracks()[0];
            if (videoTrack) {
                videoTrack.enabled = !videoTrack.enabled;
                setVideoEnabled(videoTrack.enabled);
            }
        }
    };

    const pararTransmissao = () => {
        if (localStreamRef.current) {
            localStreamRef.current.getTracks().forEach((track) => track.stop());
            localStreamRef.current = null;
        }
        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        desconectarSocket();
        stopTimer();
        setIsStreaming(false);
        setStatusText('Transmissão encerrada.');
        setStatusColor('text.secondary');
        setViewersCount(0);
    };

    const handleSendMessage = (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !socketRef.current) return;

        socketRef.current.emit('enviar_mensagem', {
            canal: canal.trim(),
            texto: newMessage
        });

        setNewMessage('');
    };

    const alternarFixacaoProduto = (produto) => {
        const novoProduto = pinnedProduct?.id === produto.id ? null : produto;
        setPinnedProduct(novoProduto);

        if (socketRef.current) {
            socketRef.current.emit('fixar_produto', {
                canal: canal.trim(),
                produto: novoProduto
            });
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 2 }}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Paper elevation={3} sx={{ p: 2, borderRadius: 3, position: 'relative', bgcolor: '#121212', color: '#fff' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TextField
                                    size="small"
                                    label="Canal / Loja"
                                    value={canal}
                                    onChange={(e) => setCanal(e.target.value)}
                                    disabled={isStreaming}
                                    sx={{
                                        bgcolor: '#fff',
                                        borderRadius: 1,
                                        width: 150,
                                        '& .MuiInputBase-input': { fontWeight: 'bold' }
                                    }}
                                />
                                <Chip
                                    label={statusText}
                                    sx={{
                                        bgcolor: statusColor === 'error.main' ? '#d32f2f' : statusColor === 'warning.main' ? '#ed6c02' : '#424242',
                                        color: '#fff',
                                        fontWeight: 'bold'
                                    }}
                                />
                            </Box>

                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="h6" sx={{ color: '#ff1744', fontFamily: 'monospace', fontWeight: 'bold' }}>
                                    {formatTime(secondsElapsed)}
                                </Typography>
                                <Chip icon={<VisibilityIcon sx={{ color: '#fff !important' }} />} label={viewersCount} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                                <Chip icon={<FavoriteIcon sx={{ color: '#ff4081 !important' }} />} label={likesCount} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                            </Stack>
                        </Stack>

                        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: '#000' }}>
                            <Box
                                component="video"
                                ref={videoRef}
                                autoPlay
                                muted
                                playsInline
                                sx={{
                                    width: '100%',
                                    height: { xs: '380px', sm: '500px' },
                                    objectFit: 'cover',
                                    transform: usingFrontCamera ? 'scaleX(-1)' : 'none',
                                    display: 'block'
                                }}
                            />

                            {pinnedProduct && (
                                <Card
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        left: 16,
                                        display: 'flex',
                                        maxWidth: 280,
                                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                                        backdropFilter: 'blur(5px)',
                                        boxShadow: 6
                                    }}
                                >
                                    <CardMedia component="img" sx={{ width: 70, height: 70, objectFit: 'cover' }} image={pinnedProduct.img} alt={pinnedProduct.nome} />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', p: 1, justifyContent: 'center' }}>
                                        <Typography variant="caption" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PinIcon fontSize="inherit" /> Em Destaque
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold" noWrap>{pinnedProduct.nome}</Typography>
                                        <Typography variant="body2" color="success.main" fontWeight="bold">{pinnedProduct.preco}</Typography>
                                    </Box>
                                </Card>
                            )}
                        </Box>

                        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                            {!isStreaming ? (
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="large"
                                    startIcon={<VideocamIcon />}
                                    onClick={iniciarTransmissao}
                                    sx={{ fontWeight: 'bold', px: 4, py: 1.2, borderRadius: 3 }}
                                >
                                    Iniciar Transmissão
                                </Button>
                            ) : (
                                <>
                                    <Button variant="contained" color="info" startIcon={<SwitchCameraIcon />} onClick={trocarCamera}>
                                        Câmera
                                    </Button>
                                    <Button variant="contained" color={audioEnabled ? 'info' : 'warning'} startIcon={audioEnabled ? <MicIcon /> : <MicOffIcon />} onClick={toggleAudio}>
                                        {audioEnabled ? 'Mutar' : 'Desmutar'}
                                    </Button>
                                    <Button variant="contained" color={videoEnabled ? 'info' : 'warning'} startIcon={videoEnabled ? <VideocamIcon /> : <VideocamOffIcon />} onClick={toggleVideo}>
                                        {videoEnabled ? 'Pausar Vídeo' : 'Retomar'}
                                    </Button>
                                    <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={pararTransmissao} sx={{ fontWeight: 'bold' }}>
                                        Encerrar Live
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Stack spacing={2}>
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 3, height: 320, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle1" fontWeight="bold" borderBottom="1px solid #eee" pb={1} mb={1}>
                                Chat da Live
                            </Typography>

                            <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                                <List size="small" disablePadding>
                                    {messages.map((msg) => (
                                        <ListItem key={msg.id} alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                                            <ListItemAvatar sx={{ minWidth: 36 }}>
                                                <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: msg.isLojista ? '#1976d2' : '#bdbdbd' }}>
                                                    {msg.user[0]}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="caption" fontWeight="bold" color={msg.isLojista ? 'primary.main' : 'text.primary'}>
                                                        {msg.user}
                                                    </Typography>
                                                }
                                                secondary={<Typography variant="body2" color="text.secondary">{msg.text}</Typography>}
                                            />
                                        </ListItem>
                                    ))}
                                    <div ref={chatBottomRef} />
                                </List>
                            </Box>

                            <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 1, pt: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Responder no chat..."
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    disabled={!isStreaming}
                                />
                                <IconButton color="primary" type="submit" disabled={!isStreaming || !newMessage.trim()}>
                                    <SendIcon />
                                </IconButton>
                            </Box>
                        </Paper>

                        {/* Painel de Gestão de Produtos em Destaque */}
                        <Paper elevation={3} sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                                Destaques da Vitrine
                            </Typography>
                            <Stack spacing={1}>
                                {produtos.map((prod) => (
                                    <Card key={prod.id} variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 1, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar src={prod.img} variant="rounded" sx={{ width: 40, height: 40 }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">{prod.nome}</Typography>
                                                <Typography variant="caption" color="success.main" fontWeight="bold">{prod.preco}</Typography>
                                            </Box>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant={pinnedProduct?.id === prod.id ? 'contained' : 'outlined'}
                                            color={pinnedProduct?.id === prod.id ? 'secondary' : 'primary'}
                                            startIcon={<PushPinIcon />}
                                            onClick={() => alternarFixacaoProduto(prod)}
                                            disabled={!isStreaming}
                                        >
                                            {pinnedProduct?.id === prod.id ? 'Fixado' : 'Fixar'}
                                        </Button>
                                    </Card>
                                ))}
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>
        </Container>
    );
};

export default LiveStreamLojista;